import { samanDoctorAvailabilityStatus } from '@/common/apis/services/booking/samanDoctorAvailabilityStatus';
import { useSearch } from '@/common/apis/services/search/search';
import { ServerStateKeysEnum } from '@/common/apis/serverStateKeysEnum';
import Button from '@/common/components/atom/button';
import Card from '@/common/components/atom/card';
import Skeleton from '@/common/components/atom/skeleton';
import Text from '@/common/components/atom/text/text';
import useCustomize from '@/common/hooks/useCustomize';
import { splunkInstance } from '@/common/services/splunk';
import RecommendCard from '@/modules/booking/components/recommend/card/card';
import { doctorSlugFromSearchHit, useRaviCardRate } from '@/modules/profile/hooks/useDoctorRateDetails';
import SearchCard from '@/modules/search/components/card/card';
import { useQueries } from '@tanstack/react-query';
import random from 'lodash/random';
import { useEffect, useMemo, useRef } from 'react';
import { LocalPrivateSpecialistsConfig, PRIVATE_OFFICE_RESULT_TYPE, localPrivateSpecialistsTitle } from './config';
import { LocalSpecialistsEventType, LocalSpecialistsPlacement, buildLocalSpecialistsEvent } from './events';
import { firstTurnLabel, hasIndexedInPersonTurn, rankLocalSpecialists } from './rankLocalSpecialists';

const searchPageUrl = (config: LocalPrivateSpecialistsConfig) =>
  `/s/${config.city}/${config.groupSlug}/?result_type=${encodeURIComponent(PRIVATE_OFFICE_RESULT_TYPE)}`;

const slugOf = (doctor: any) => doctorSlugFromSearchHit(doctor) ?? '';

/**
 * Same-city private-office specialists of the profile's specialty that can be booked in person right
 * now, best-rated first. Search gives the candidates (ranked, pre-filtered on its first-turn index);
 * each candidate is then confirmed with Saman — the same availability source the profile's own booking
 * card uses — and only `booking_available` doctors are kept, so nobody whose booking is closed or full
 * is ever suggested. Analytics: see events.ts.
 */
const useLocalPrivateSpecialists = (
  config: LocalPrivateSpecialistsConfig,
  ownSlug: string,
  placement: LocalSpecialistsPlacement,
) => {
  const customize = useCustomize(state => state.customize);
  const enabled = !customize.partnerKey;
  const searchData = useSearch(
    {
      route: `${config.city}/${config.groupSlug}`,
      query: { result_type: PRIVATE_OFFICE_RESULT_TYPE, limit: 50 },
    },
    { enabled },
  );
  const candidates = useMemo(
    () => rankLocalSpecialists(searchData.data?.search?.result, config, ownSlug).filter(hasIndexedInPersonTurn),
    [searchData.data, config, ownSlug],
  );
  // Same key as useSamanDoctorAvailabilityStatus, so a later visit to that profile reuses the answer.
  const availability = useQueries({
    queries: candidates.map(hit => {
      const slug = slugOf(hit);
      return {
        queryKey: [ServerStateKeysEnum.SamanDoctorAvailabilityStatus, slug],
        queryFn: () => samanDoctorAvailabilityStatus(slug),
        enabled: enabled && !!slug,
        staleTime: 60_000,
        refetchOnWindowFocus: false,
      };
    }),
  });

  const ready = searchData.isSuccess && availability.every(result => !result.isLoading || result.fetchStatus === 'idle');
  const doctors = useMemo(
    () => candidates.filter((_, index) => availability[index]?.data?.in_person_availability?.booking_available === true),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [candidates, availability.map(result => result.dataUpdatedAt).join()],
  );

  const track = (type: LocalSpecialistsEventType, fields?: Record<string, unknown>) =>
    splunkInstance('search').sendEvent(buildLocalSpecialistsEvent(type, { slug: ownSlug, placement, config }, fields));

  // Nobody to suggest (or search failed): counted once, so views can be read against all openings.
  const emptySent = useRef(false);
  const searchFailed = enabled && searchData.isError;
  const noneBookable = enabled && ready && !doctors.length;
  useEffect(() => {
    if (emptySent.current || (!searchFailed && !noneBookable)) return;
    emptySent.current = true;
    track('empty', { reason: searchFailed ? 'search_error' : 'no_bookable', candidate_count: candidates.length });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchFailed, noneBookable]);

  const hidden = !enabled || searchFailed || noneBookable;
  return { doctors, candidateCount: candidates.length, isLoading: !ready, hidden, track };
};

/** `view`, once per mount, with the doctors actually on screen. */
const useViewEvent = (
  shown: any[] | undefined,
  extra: { bookable_count: number; candidate_count: number },
  track: (type: LocalSpecialistsEventType, fields?: Record<string, unknown>) => unknown,
) => {
  const sent = useRef(false);
  useEffect(() => {
    if (sent.current || !shown?.length) return;
    sent.current = true;
    track('view', { shown_doctors: shown.map(slugOf), shown_count: shown.length, ...extra });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown]);
};

interface Props {
  config: LocalPrivateSpecialistsConfig;
  /** The profile's own doctor — never listed. */
  ownSlug: string;
}

/**
 * Booking «نوبت پر شده» modal: the same carousel the modal shows for online doctors (Recommend), with
 * this city's bookable private specialists and their first free turn right above «دریافت نوبت».
 */
export const LocalPrivateSpecialistsRecommend = ({ config, ownSlug }: Props) => {
  const { doctors, candidateCount, isLoading, hidden, track } = useLocalPrivateSpecialists(config, ownSlug, 'booking-full');
  useViewEvent(isLoading ? undefined : doctors, { bookable_count: doctors.length, candidate_count: candidateCount }, track);
  if (hidden) return null;

  const idOf = (doctor: any) => doctor.id ?? doctor.url;
  const handleClickDoctor = (id: string, element?: string) => {
    const position = doctors.findIndex(doctor => idOf(doctor) === id);
    if (position < 0) return;
    const doctor = doctors[position];
    track('click-doctor', { doctor: slugOf(doctor), position: position + 1, element, first_turn: firstTurnLabel(doctor) });
  };

  return (
    <div className="flex flex-col space-y-3" data-testid="local-private-specialists">
      <Text fontSize="sm" fontWeight="bold" className="leading-6">
        {localPrivateSpecialistsTitle(config)}
      </Text>
      {isLoading ? (
        <div className="flex pb-5 overflow-auto no-scroll space-s-3">
          <Skeleton w="15rem" h="13rem" rounded="lg" className="min-w-[15rem]" />
          <Skeleton w="15rem" h="13rem" rounded="lg" />
        </div>
      ) : (
        <RecommendCard
          listOfDoctors={doctors.map(doctor => ({
            image: doctor.image,
            displayExpertise: doctor.display_expertise,
            displayName: doctor.title ?? doctor.display_name,
            isBulk: doctor.is_bulk,
            ratesCount: doctor.rates_count,
            rate: doctor.satisfaction,
            url: doctor.url,
            id: idOf(doctor),
            action: [{ title: 'دریافت نوبت', description: firstTurnLabel(doctor), outline: false }],
          }))}
          clickRecommendEvent={handleClickDoctor}
          showMore={{ text: `همه پزشکان ${config.expertiseName} ${config.cityName}`, url: searchPageUrl(config) }}
          onShowMoreClick={() => track('click-more', { target: searchPageUrl(config) })}
        />
      )}
    </div>
  );
};

/**
 * Profile, in its own card under the no-booking card: one doctor as the old substitute suggestion
 * showed it (banner + card + «مشاهده سایر …» link), picked among the three best-rated bookable ones.
 */
export const LocalPrivateSpecialistSuggestion = ({ config, ownSlug }: Props) => {
  const { doctors, candidateCount, isLoading, hidden, track } = useLocalPrivateSpecialists(config, ownSlug, 'profile');
  const position = useMemo(
    () => (!isLoading && doctors.length ? random(0, Math.min(2, doctors.length - 1)) : -1),
    [doctors, isLoading],
  );
  const doctor = position >= 0 ? doctors[position] : undefined;
  const shown = useMemo(() => (doctor ? [doctor] : undefined), [doctor]);
  useViewEvent(shown, { bookable_count: doctors.length, candidate_count: candidateCount }, track);
  const raviRate = useRaviCardRate(doctor ? slugOf(doctor) : undefined);
  if (hidden || !doctor) return null;

  const openDoctor = (element: string) => {
    track('click-doctor', { doctor: slugOf(doctor), position: position + 1, element, first_turn: firstTurnLabel(doctor) });
    location.assign(doctor.url);
  };
  const openMore = () => {
    const target = searchPageUrl(config);
    track('click-more', { target });
    location.assign(target);
  };

  return (
    <Card className="space-y-2 !rounded-none md:!rounded-lg" data-testid="local-private-specialists">
      <div className="px-3 py-2 text-white bg-green-600 border border-green-700 rounded-md">
        <Text className="text-sm font-bold leading-6">{localPrivateSpecialistsTitle(config)}</Text>
      </div>
      <div onClick={event => !(event.target as HTMLElement).closest('button') && openDoctor('card')}>
        <SearchCard
          avatarSize="lg"
          type="doctor"
          className="shadow-none !py-0 lg:!py-0 cursor-pointer"
          baseInfo={{
            displayName: doctor.title,
            expertise: doctor.display_expertise,
            avatar: doctor.image,
            isVerify: !doctor.is_bulk,
            rate: raviRate,
          }}
          actions={[
            {
              text: `دریافت نوبت از دکتر ${doctor.title}`,
              outline: false,
              description: firstTurnLabel(doctor),
              action: () => openDoctor('button'),
            },
          ]}
        />
      </div>
      <Button block size="sm" className="text-xs opacity-70" variant="text" onClick={openMore}>
        مشاهده سایر پزشکان {config.expertiseName} {config.cityName}
      </Button>
    </Card>
  );
};
